import { useState, useCallback, useRef } from 'react';
import { recurringEntriesApi } from '../services/api/endpoints/recurringEntries';
import type {
  RecurringEntry,
  CreateRecurringEntryPayload,
  UpdateRecurringEntryPayload,
  GenerateRecurringResult,
} from '../types/recurring-entries';

export function useRecurringEntries() {
  const [entries, setEntries] = useState<RecurringEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);

  const loadEntries = useCallback(async (params?: { isActive?: boolean; frequency?: string }) => {
    const requestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const response = await recurringEntriesApi.getAll(params);

      if (requestId !== loadRequestIdRef.current) return;

      if (response && 'data' in response) {
        const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
        setEntries(data);
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err.message || 'Erreur lors du chargement des écritures récurrentes');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const createEntry = useCallback(async (data: CreateRecurringEntryPayload): Promise<RecurringEntry | null> => {
    try {
      setError(null);
      const response = await recurringEntriesApi.create(data);
      if (response && 'data' in response) {
        const newEntry = (response.data as any)?.data || response.data;
        setEntries(prev => [newEntry as RecurringEntry, ...prev]);
        return newEntry as RecurringEntry;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
      return null;
    }
  }, []);

  const updateEntry = useCallback(async (id: string, data: UpdateRecurringEntryPayload): Promise<RecurringEntry | null> => {
    try {
      setError(null);
      const response = await recurringEntriesApi.update(id, data);
      if (response && 'data' in response) {
        const updated = (response.data as any)?.data || response.data;
        setEntries(prev => prev.map(e => e.id === id ? updated as RecurringEntry : e));
        return updated as RecurringEntry;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
      return null;
    }
  }, []);

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);
      await recurringEntriesApi.delete(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  const generateEntries = useCallback(async (): Promise<GenerateRecurringResult | null> => {
    try {
      setError(null);
      const response = await recurringEntriesApi.generate();
      if (response && 'data' in response) {
        const result = (response.data as any)?.data || response.data;
        // Reload entries to reflect updated nextRunDate
        await loadEntries();
        return result as GenerateRecurringResult;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la génération');
      return null;
    }
  }, [loadEntries]);

  // Computed values
  const activeEntries = entries.filter(e => e.isActive);
  const dueEntries = activeEntries.filter(e => new Date(e.nextRunDate) <= new Date());

  return {
    entries,
    loading,
    error,
    loadEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    generateEntries,
    activeEntries,
    dueEntries,
  };
}
